// ─── src/NpdRsvpsView.tsx ────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import {
  Building2,
  Download,
  Mail,
  Phone,
  Search,
  Trash2,
  Users,
  Utensils,
  X,
} from "lucide-react";
import {
  useDeleteNpdRsvp,
  useNpdRsvpStats,
  useNpdRsvps,
} from "./hooks/useNpd";
import { Pagination } from "./components/dashboard/Pagination";
import { TableSkeleton } from "./components/ui/Skeleton";
import { StatCard } from "./components/ui/StatCard";
import type { NpdInvestorRsvp } from "./api/npd";

const LIMIT = 20;

const CSV_HEADERS = [
  "Full Name",
  "Role",
  "Firm",
  "Email",
  "Phone",
  "Guests",
  "Dietary Notes",
  "Submitted At",
];

function exportCsv(rows: NpdInvestorRsvp[]) {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = rows.map((r) =>
    [
      r.fullName,
      r.role,
      r.firmName,
      r.email,
      r.phone,
      r.plusOnes ?? "",
      r.dietaryNotes ?? "",
      r.createdAt,
    ]
      .map(esc)
      .join(","),
  );
  const blob = new Blob(["﻿" + [CSV_HEADERS.join(","), ...body].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `npd_rsvps_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function NpdRsvpsView() {
  const [search, setSearch] = useState("");
  const [firmName, setFirmName] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<NpdInvestorRsvp | null>(null);

  const { data: stats } = useNpdRsvpStats();
  const { data: allData } = useNpdRsvps({ page: 1, limit: 2000 });
  const {
    data: tableData,
    isLoading,
    isFetching,
    isError,
  } = useNpdRsvps({ page, limit: LIMIT, search, firmName });

  const { mutate: deleteRow, isPending: isDeleting } = useDeleteNpdRsvp();

  const rows = tableData?.data ?? [];
  const meta = tableData?.meta ?? { total: 0, page: 1, limit: LIMIT, totalPages: 1 };
  const firmOptions = Object.keys(stats?.byFirm ?? {}).sort();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label="Confirmed RSVPs"
          value={stats?.total ?? 0}
          icon={Users}
          accent="bg-brand-orange"
        />
        {firmOptions.slice(0, 3).map((f) => (
          <StatCard
            key={f}
            label={f}
            value={stats?.byFirm[f] ?? 0}
            icon={Building2}
            accent="bg-brand-teal"
          />
        ))}
      </div>

      <div className="card p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search name, firm, email…"
            className="w-full pl-9 pr-8 py-2 text-sm bg-brand-navy border border-brand-border rounded-lg text-white placeholder-brand-muted focus:outline-none focus:border-brand-green"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted hover:text-white"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <select
          value={firmName}
          onChange={(e) => {
            setFirmName(e.target.value);
            setPage(1);
          }}
          className="py-2 px-3 text-sm bg-brand-navy border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green"
        >
          <option value="">All firms</option>
          {firmOptions.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
        <button
          onClick={() => exportCsv(allData?.data ?? [])}
          disabled={(allData?.data ?? []).length === 0}
          className="btn-primary text-sm flex items-center gap-1.5 ml-auto disabled:opacity-50"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <div className="card p-8 text-center text-red-400">Failed to load RSVPs.</div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center text-brand-muted">No RSVPs yet.</div>
      ) : (
        <>
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-brand-border text-left text-brand-muted">
                  <th className="px-4 py-3 font-medium">#</th>
                  <th className="px-4 py-3 font-medium">Investor</th>
                  <th className="px-4 py-3 font-medium">Firm</th>
                  <th className="px-4 py-3 font-medium">Guests</th>
                  <th className="px-4 py-3 font-medium">Dietary</th>
                  <th className="px-4 py-3 font-medium">Submitted</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.id}
                    onClick={() => setSelected(r)}
                    className="border-b border-brand-border/50 hover:bg-brand-border/20 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3 text-brand-muted">
                      {(meta.page - 1) * LIMIT + i + 1}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">{r.fullName}</div>
                      <div className="text-brand-muted text-xs">{r.role}</div>
                    </td>
                    <td className="px-4 py-3 text-brand-muted">{r.firmName}</td>
                    <td className="px-4 py-3 text-brand-muted">
                      {r.plusOnes || "—"}
                    </td>
                    <td className="px-4 py-3 text-brand-muted">
                      {r.dietaryNotes ? (
                        <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-brand-orange/15 text-brand-orange border border-brand-orange/25">
                          <Utensils size={11} /> Yes
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                      {new Date(r.createdAt).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm("Delete this RSVP?"))
                            deleteRow(r.id);
                        }}
                        disabled={isDeleting}
                        className="p-1.5 rounded-lg text-brand-muted hover:text-red-400 hover:bg-red-500/10 disabled:opacity-40"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={meta.page}
            totalPages={meta.totalPages}
            total={meta.total}
            limit={LIMIT}
            onPageChange={setPage}
            isFetching={isFetching}
          />
        </>
      )}

      {selected && (
        <RsvpDetailModal rsvp={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function RsvpDetailModal({
  rsvp,
  onClose,
}: {
  rsvp: NpdInvestorRsvp;
  onClose: () => void;
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="card w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl shadow-black/40 animate-in fade-in-0 slide-in-from-bottom-4 duration-200">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-brand-border bg-brand-card px-6 py-4">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-brand-muted">
              {rsvp.role}
            </p>
            <h2 className="mt-1 text-xl font-bold text-white">{rsvp.fullName}</h2>
            <p className="text-sm text-brand-muted">{rsvp.firmName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border/40"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-brand-muted mb-1">
                <Mail size={12} /> Email
              </p>
              <p className="text-white text-sm break-words">{rsvp.email}</p>
            </div>
            <div>
              <p className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-brand-muted mb-1">
                <Phone size={12} /> Phone
              </p>
              <p className="text-white text-sm">{rsvp.phone}</p>
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-wider text-brand-muted mb-1">
              Guests joining
            </p>
            <p className="text-white text-sm">{rsvp.plusOnes || "None"}</p>
          </div>

          {rsvp.dietaryNotes && (
            <div>
              <p className="text-xs uppercase tracking-wider text-brand-muted mb-1">
                Dietary notes
              </p>
              <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">
                {rsvp.dietaryNotes}
              </p>
            </div>
          )}

          <p className="text-xs text-brand-muted">
            Submitted {new Date(rsvp.createdAt).toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
}
